import PinReducer from "./pinStore/reducer/pinReducer";
import pinVerificationReducer from "./pinVerificationSlice";
import {persistReducer, persistStore} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import {combineReducers, configureStore} from '@reduxjs/toolkit';
import NotificationCounterReducer from "./NotificationCounter/reducer/notificationCounterReducer";
import NotificationListReducer from "./NotificationList/reducer/NotificationListReducer";
import MessageCounterReducer from "./MessageCounter/reducer/MessageCounterReducer";
import staticDataReducer from "./staticData/reducer/staticDataReducer";
import clientAccountPaymentReducer from "./clientAccountPaymentSlice";

const PERSIST_KEY = 'persist:root';

/**
 * Removes PIN material written to localStorage by earlier versions of the app:
 * the standalone "pin" key, and the `pinRx` slice persisted inside persist:root.
 * Runs before the store is created so nothing rehydrates from it. Safe on every
 * boot — a no-op once storage is clean.
 */
const purgeLegacyPinStorage = (): void => {
    try {
        localStorage.removeItem('pin');
        const persisted = localStorage.getItem(PERSIST_KEY);
        if (!persisted) return;
        const parsed = JSON.parse(persisted);
        if (parsed && Object.prototype.hasOwnProperty.call(parsed, 'pinRx')) {
            delete parsed.pinRx;
            localStorage.setItem(PERSIST_KEY, JSON.stringify(parsed));
        }
    } catch (e) {
        // Storage unavailable or persist:root corrupt — nothing to purge.
        console.error('purgeLegacyPinStorage:', e);
    }
};

purgeLegacyPinStorage();

const persistConfig = {
    key: 'root',
    storage,
    // The raw PIN is a credential and must never reach localStorage. `pinRx` is
    // held in memory only, for the duration of the verification flow.
    blacklist: ['pinRx'],
};

const rootReducer = {
    pinRx: PinReducer,
    pinVerification: pinVerificationReducer,
    notificationCounter: NotificationCounterReducer,
    notificationList: NotificationListReducer,
    messageCounter: MessageCounterReducer,
    staticData: staticDataReducer,
    clientAccountPayment: clientAccountPaymentReducer
};

const persistedReducer = persistReducer(persistConfig, combineReducers(rootReducer));

export const store = configureStore({
    reducer: persistedReducer,
});
export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;